import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from 'react';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Box } from '@uhg-abyss/web/ui/Box';
import {
  useLazyGetUserDetailsQuery,
  useLazyGetAuthenticationQuery,
  useLazyGetAuthenticationWelcomepingQuery,
  useLazyRefreshTokenQuery,
} from '@src/store/services/LoginServices';
import { LoadingScreen } from '@src/common/LoadingScreen';
import { getServiceURL } from '@src/env.config';
import { removeJWTToken, getJWTExpiry } from '@src/utils/tokenManager';

export type User = {
  userId: string;
  userType: 'I' | 'E';
  userRole: number;
  userStatus: 'A' | 'I';
  cdbAccess: boolean;
  gpsAccess: boolean;
  primeAccess: boolean;
  onlyPrimeAccess: boolean;
  addProfile: boolean;
  copyProfile: boolean;
  deleteProfile: boolean;
  editAnnouncePage: boolean;
  editProfile: boolean;
  editRolePage: boolean;
  moveProfile: boolean;
  primeRedirectUrl: string | null;
  userAccessRole: string;
  userFavourites: true;
  viewMntr: boolean;
  viewProfile: boolean;
  thrshRepr: boolean;
  cmplRepr: boolean;
  maskingType: 0 | 1 | 2 | 3;
  defaultAccess: string;
};

export type UserObj = Pick<User, 'userId' | 'userType'>;

type AuthContextType = {
  user: User | null;
  userObj?: UserObj | null;
  userMap: UserObj[];
  userAccessCall: (userObj?: UserObj | null) => void;
  userAuthCall: (code: string, welcomeping?: boolean) => void;
  logout: () => void;
  isAuth: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
// Timer references for inactivity, warning popup and token expiry
let inactivityTimer: any = null;
let tokenExpiryTimer: any = null;
let warningTimer: any = null;

export const EE_USER = 'ee_i_user';
export const EE_USER_OBJ = 'ee_i_user_obj';
export const EE_USER_MAP = 'ee_i_user_map';
export const EE_SERVICE = 'ee_service';

const getStorageItem = (key: string, defaultValue?: any) => {
  const rawStorageItem = localStorage.getItem(key);
  if (!rawStorageItem) return defaultValue;
  return JSON.parse(atob(rawStorageItem));
};

const setStorageItem = (key: string, value: any) => {
  const encodedValue = btoa(JSON.stringify(value));
  localStorage.setItem(key, encodedValue);
};

const getOHCIDUrl = () => {
  return `https://${`${window.location.hostname}/gpsservice/api/user-acl/login`}`;
};

const INACTIVITY_LIMIT = 15 * 60 * 1000;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [userObj, setUserObj] = useState<UserObj | null>(null);
  const [userMap, setUserMap] = useState<UserObj[]>([]);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 seconds countdown
  const router = useRouter();
  const [searchParams] = router.getSearchParams();
  const [userTrigger, { isLoading: userLoading }] =
    useLazyGetUserDetailsQuery();
  const [authTrigger, { isLoading: authLoading }] =
    useLazyGetAuthenticationQuery();
  const [welcomepingAuthTrigger, { isLoading: pingAuthLoading }] =
    useLazyGetAuthenticationWelcomepingQuery();
  const [refreshTokenTrigger] = useLazyRefreshTokenQuery();
  const isAuth = !!user;

  useEffect(() => {
    setUser(getStorageItem(EE_USER));
    setUserObj(getStorageItem(EE_USER_OBJ));
    setUserMap(getStorageItem(EE_USER_MAP, []));
  }, []);

  const inactiveFn = () => {
    inactivityTimer = setTimeout(() => {
      setIsWarningVisible(true);
      setCountdown(60);
      warningTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 0) {
            logout();
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_LIMIT);
  };

  const handleUserActivity = () => {
    if (inactivityTimer && !warningTimer) {
      clearTimeout(inactivityTimer);
      inactiveFn();
    }

    if (!inactivityTimer && !warningTimer) inactiveFn();
  };

  useEffect(() => {
    const resetActivity = () => {
      handleUserActivity();
    };
    if (isAuth) {
      document.addEventListener('mousemove', resetActivity);
      document.addEventListener('keydown', resetActivity);
      document.addEventListener('click', resetActivity);
      resetActivity();
      return () => {
        document.removeEventListener('mousemove', resetActivity);
        document.removeEventListener('keydown', resetActivity);
        document.removeEventListener('click', resetActivity);
      };
    } else {
      document.removeEventListener('mousemove', resetActivity);
      document.removeEventListener('keydown', resetActivity);
      document.removeEventListener('click', resetActivity);
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (warningTimer) clearTimeout(warningTimer);
    }
  }, [isAuth]);

  const userAccessCall = async (userObj: UserObj) => {
    try {
      const userResp = await userTrigger(userObj);
      if (userResp?.data?.onlyPrimeAccess) {
        window.location.href = userResp?.data?.primeRedirectUrl;
        return;
      }
      if (
        !userResp?.data?.cdbAccess &&
        !userResp?.data?.gpsAccess &&
        userResp?.data?.primeAccess
      ) {
        primeCallHandler(userResp?.data);
        return;
      }
      if (userResp?.data?.userStatus !== 'A') {
        router.navigate('/deactivated');
        return;
      }
      if (
        !userResp?.data?.cdbAccess &&
        !userResp?.data?.gpsAccess &&
        userResp?.data?.defaultAccess === 'NO_ACCESS'
      ) {
        router.navigate('/no-access');
        return;
      }
      setUser(userResp?.data || null);
      setUserObj(userObj);
      setStorageItem(EE_USER, userResp?.data);
      setStorageItem(EE_USER_OBJ, userObj);
      scheduleTokenExpiryWarning();
      router.navigate('/');
    } catch (e) {
      router.navigate('/error');
    }
  };

  const primeCallHandler = async (userResp) => {
    // PRIME redirect API (getPrimeRedirectUri) has been decommissioned - PRIME access
    // via this flow is no longer available.
    console.log(
      'PRIME redirect is unavailable: getPrimeRedirectUri has been decommissioned.',
      userResp?.userId
    );
  };

  const userAuthCall = async (code: string, isWelcomePing?: boolean) => {
    try {
      const resp = await (isWelcomePing
        ? welcomepingAuthTrigger(code)
        : authTrigger(code));
      if (!resp?.data) {
        router.navigate('/error');
        return;
      }
      const { validToken, multiUser, mappedUsers } = resp.data as any;
      if (!validToken) router.navigate('/invalid');
      if (mappedUsers) {
        setStorageItem(EE_USER_MAP, mappedUsers);
        setUserMap(mappedUsers);
      }
      if (validToken && !multiUser && mappedUsers?.[0]) {
        userAccessCall(mappedUsers?.[0]);
      }
    } catch {
      router.navigate('/error');
    }
  };

  const scheduleTokenExpiryWarning = () => {
    if (tokenExpiryTimer) clearTimeout(tokenExpiryTimer);
    const expiry = getJWTExpiry();
    if (!expiry) return;
    // Auto-refresh token 30 seconds before it expires
    const delay = expiry - Date.now() - 30000;
    tokenExpiryTimer = setTimeout(async () => {
      try {
        const resp: any = await refreshTokenTrigger(undefined);
        // Token is delivered as an HttpOnly cookie, so success is signalled by the returned expiry.
        const refreshed = !!resp?.data?.expirationTime;
        const hasError = resp?.data?.error || resp?.error;

        if (refreshed && !hasError) {
          // Token refreshed successfully, schedule next refresh
          console.log('Token refreshed successfully');
          scheduleTokenExpiryWarning();
        } else {
          // Failed to refresh, logout
          console.error(
            'Token refresh failed:',
            hasError || 'No token in response'
          );
          logout();
        }
      } catch (error) {
        console.error('Token refresh exception:', error);
        logout();
      }
    }, Math.max(delay, 0));
  };

  const logout = () => {
    setUser(null);
    setUserObj(null);
    setUserMap([]);
    setIsWarningVisible(false);
    clearTimeout(inactivityTimer);
    clearTimeout(warningTimer);
    clearTimeout(tokenExpiryTimer);
    tokenExpiryTimer = null;

    localStorage.removeItem(EE_USER);
    localStorage.removeItem(EE_USER_OBJ);
    localStorage.removeItem(EE_USER_MAP);
    localStorage.removeItem('globalFilter');
    // Clear JWT token on logout
    removeJWTToken();
    router.navigate('/logout');
  };

  const dismissWarning = () => {
    setIsWarningVisible(false);
    setCountdown(60);
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (warningTimer) clearTimeout(warningTimer);
    handleUserActivity();
  };

  useEffect(() => {
    const uObj = getStorageItem(EE_USER_OBJ);
    const OHCURl = getOHCIDUrl();

    if (
      window?.location?.href?.includes('ohcid') ||
      window?.location?.href?.includes('monitor') ||
      window?.location?.href?.includes('eeseems')
    ) {
      const isPing = window?.location?.href?.includes('eeseems');
      const code = isPing
        ? searchParams.get('opentoken')
        : searchParams.get('code');
      if (code) userAuthCall(code, isPing);
      return;
    }
    if (uObj) {
      (async () => {
        try {
          const userResp = await userTrigger(uObj);
          if (userResp?.data?.onlyPrimeAccess) {
            window.location.href = userResp?.data?.primeRedirectUrl;
            return;
          }
          if (
            !userResp?.data?.cdbAccess &&
            !userResp?.data?.gpsAccess &&
            userResp?.data?.primeAccess
          ) {
            primeCallHandler(userResp?.data);
            return;
          }
          if (userResp?.data?.userStatus !== 'A') {
            window.location.href = OHCURl;
            return;
          }
          setUser(userResp?.data || null);
          setStorageItem(EE_USER, userResp?.data);
          setStorageItem(EE_USER_OBJ, uObj);
          scheduleTokenExpiryWarning();
        } catch (e) {
          console.log(e);
          window.location.href = OHCURl;
        }
      })();
    } else {
      window.location.href = OHCURl;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userObj,
        userMap,
        userAccessCall,
        userAuthCall,
        isAuth,
        logout,
      }}
    >
      {userLoading || authLoading || pingAuthLoading ? (
        <LoadingScreen fullscreen />
      ) : (
        <>
          <ModalDialog
            isOpen={isWarningVisible}
            onClose={dismissWarning}
            header="Session Timeout Warning"
          >
            <div>
              <div className="popup">
                <p>
                  Your session will expire in{' '}
                  <span style={{ color: 'red' }}>{countdown}</span> seconds due
                  to inactivity.
                </p>
                <Box
                  css={{
                    padding: 0,
                    background: 'transparent',
                    display: 'flex',
                    gap: '12px',
                    marginTop: '20px',
                    alignItems: 'center',
                  }}
                >
                  <Button onClick={dismissWarning}>Stay Logged In</Button>
                  <Button onClick={logout} variant="filled" color="destructive">
                    Logout
                  </Button>
                </Box>
              </div>
            </div>
          </ModalDialog>
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};
